<template>
  <div class="w-full flex flex-col items-center p-5 mx-auto min-w-0" style="max-width: 940px;">
    <!-- Annual Upgrade Bonus Banner -->
    <div v-if="getBasePlanInterval !== 'year'" class="w-full flex items-center p-2 rounded-2xl bg-neutral-25">
      <GiftBoxIcon class="text-icon-normal ml-1.5" />
      <BaseText type="label" size="sm" class="text-text-muted ml-1 whitespace-nowrap">
        Annual Plan Bonus
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal ml-3 mr-1">
        Get 2x API credits when you change your base Workflow Plan billing to annual.
      </BaseText>
      <button
        class="ml-auto flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-50 whitespace-nowrap"
        @click="showSubscriptionDrawer">
        <BaseText type="label" size="sm" class="text-text-muted">
          Update Subscription
        </BaseText>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M8.33334 13.3334L11.0774 10.5893C11.4029 10.2639 11.4029 9.73626 11.0774 9.41083L8.33334 6.66675"
            stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
    <!-- Header -->
    <div class="w-full flex flex-col gap-1 mt-11">
      <BaseText type="heading" size="sm" class="text-text-muted">
        Additional API Credits
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal">
        Choose additional API credits on a monthly plan.
      </BaseText>
    </div>
    <!-- Credit Packages -->
    <div class="w-full grid grid-cols-3 gap-x-4 mt-9">
      <div v-for="(creditPackage, index) in creditPackages" :key="`package-${index}`"
        class="w-full flex flex-col gap-3 p-2.5 rounded-2xl border border-border-normal">
        <div class="w-full flex flex-col gap-3 p-1.5">
          <div class="flex items-center gap-2 pl-0.5 py-0.5 pr-3 bg-neutral-25 rounded-full w-max">
            <img src="../../assets/icons/api-token.png" class="w-7 h-7">
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ (creditPackage.creditQuantity * creditsMultiplier).toLocaleString() }} Credits
            </BaseText>
          </div>
          <div class="flex items-center gap-1">
            <div class="price-text text-text-muted">
              ${{ creditPackage.monthlyPrice }}
            </div>
            <BaseText type="body" size="sm" class="text-text-subdued">
              /month
            </BaseText>
          </div>
        </div>
        <div class="relative">
          <button @mouseenter="tooltipHovered.changePlan = creditPackage.packageId" @mouseleave="tooltipHovered.changePlan = null"
          :disabled="loading.subscribeToAPI || (getTeam && !isTeamOwner) || currentCreditPackage === creditPackage.creditQuantity"
          class="submit-btn w-full px-2.5 py-2 rounded-md flex items-center justify-center"
          @click="selectedCreditPackage = creditPackage">
            <BaseLoadingSpinnerCircle v-if="loading.subscribeToAPI === creditPackage.packageId" class="w-5 h-5 text-text-disabled" />
            <BaseText v-else type="label" size="sm"
              :class="loading.subscribeToAPI || (getTeam && !isTeamOwner) || currentCreditPackage === creditPackage.creditQuantity ? 'text-text-disabled' : 'text-white'">
              <span v-if="currentCreditPackage === creditPackage.creditQuantity">
                Current Plan
              </span>
              <span v-else>
                Add API Subscription
              </span>
            </BaseText>
            <transition>
              <div v-if="tooltipHovered.changePlan === `package-${index}` && getTeam && !isTeamOwner" class="label-tooltip">
                <BaseText type="body" size="sm" class="text-white">
                  Only account admins can access this: {{ teamOwnerEmail }}
                </BaseText>
              </div>
            </transition>
          </button>
        </div>
      </div>
    </div>
    <!-- Custom Plan -->
    <div class="w-full flex gap-7 p-7 rounded-2xl border border-border-hover mt-4 min-w-0" style="min-height: 220px;">
      <div class="h-full flex flex-col flex-shrink-0" style="width: 264px;">
        <BaseText type="label" size="md" class="text-text-muted">
          Custom
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal mt-1" style="max-width: 218px;">
          Need custom API credits? Talk with an integration expert.
        </BaseText>
        <div class="flex items-center gap-1 mt-2">
          <CoinIcon style="color: #EBBE7A;" :width="22" :height="22" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Save up to 80%
          </BaseText>
        </div>
        <a href="https://www.foreplay.co/book-demo" target="_blank"
          class="secondary-btn w-full text-center px-2.5 py-2 rounded-md mt-8">
          <BaseText type="label" size="sm" class="text-text-muted">
            Book a Call
          </BaseText>
        </a>
      </div>
      <div class="flex-grow h-full flex flex-col min-w-0">
        <div class="w-full flex flex-col gap-2.5">
          <div v-for="(incentive, index) in customPlanIncentives" :key="`incentive-${index}`"
            class="flex items-center gap-2">
            <CheckmarkIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ incentive }}
            </BaseText>
          </div>
        </div>
        <div class="w-full flex flex-col gap-2 mt-auto pt-8">
          <BaseText type="body" size="sm" class="text-text-normal">
            Trusted by over 10,000 growth teams and agencies
          </BaseText>
          <div class="w-full flex items-center justify-between flex-wrap gap-y-2">
            <img v-for="(brand, index) in trustedBrands" :key="`brand-${index}`" :src="brand" class="h-6">
          </div>
        </div>
      </div>
    </div>
    <!-- FAQ Section -->
    <div class="w-full flex gap-6 mt-11">
      <div class="flex flex-col flex-shrink-0" style="width: 280px;">
        <BaseText type="heading" size="sm" class="text-text-muted">
          FAQ
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal mt-1">
          Questions about billing and API usage.
        </BaseText>
        <button class="secondary-btn flex items-center gap-1.5 px-2.5 py-2 rounded-md w-max mt-5" @click="showIntercom">
          <ContactSupportIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Contact Support
          </BaseText>
        </button>
      </div>
      <!-- QA List -->
      <div class="flex-grow flex flex-col min-w-0">
        <div v-for="(faqItem, index) in faqItems" :key="`faq-item-${index}`"
          class="w-full flex flex-col border-b border-border-normal">
          <button class="w-full flex items-center justify-between py-5" @click="toggleFaqAnswer(index)">
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ faqItem.question }}
            </BaseText>
            <div class="transform transition-transform"
              :style="{ transform: faqItem.expanded ? 'scaleY(-1)' : 'scaleY(1)' }">
              <ChevronIcon class="text-icon-muted" />
            </div>
          </button>
          <div ref="faqAnswers" class="relative overflow-hidden">
            <transition name="answer">
              <BaseText v-if="faqItem.expanded" type="body" size="sm"
                class="text-text-normal pb-5 pr-20 whitespace-pre-line">
                {{ faqItem.answer }}
              </BaseText>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <ConfirmCreditPurchaseModal 
      v-if="selectedCreditPackage"
      :credit-package="selectedCreditPackage"
      :credits-multiplier="creditsMultiplier"
      @confirm="subscribeToAPI(selectedCreditPackage.creditQuantity, selectedCreditPackage.packageId)"
      @close="selectedCreditPackage = null"
    />
    <UpdatePaymentMethodModal v-if="showChangePayment" @close="showChangePayment = false" />
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import smoothReflow from 'vue-smooth-reflow'
import ForeplayAPI from '@/api/foreplayServer'
import FirebaseAPI from '@/api/firebase'
import _ from 'lodash'

import ConfirmCreditPurchaseModal from './ConfirmCreditPurchaseModal.vue'
import UpdatePaymentMethodModal from '../account/subscription/UpdatePaymentMethodModal.vue'

import GiftBoxIcon from '../globals/Icons/GiftBoxIcon.vue'
import CheckmarkIcon from '../globals/Icons/NewCheckmarkIcon.vue'
import ContactSupportIcon from '../globals/Icons/ContactSupportIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import CoinIcon from '../globals/Icons/BountyPayoutIcon.vue'

const creditPackages = [
  { packageId: 'package-0', creditQuantity: 100000, monthlyPrice: 99 },
  { packageId: 'package-1', creditQuantity: 250000, monthlyPrice: 189 },
  { packageId: 'package-2', creditQuantity: 500000, monthlyPrice: 349 }
]

const trustedBrands = [
  'https://publicassets.foreplay.co/api-trusted-brands/canva.png',
  'https://publicassets.foreplay.co/api-trusted-brands/gr0.png',
  'https://publicassets.foreplay.co/api-trusted-brands/hellofresh.png',
  'https://publicassets.foreplay.co/api-trusted-brands/lunar.png',
  'https://publicassets.foreplay.co/api-trusted-brands/ag1.png',
  'https://publicassets.foreplay.co/api-trusted-brands/ctc.png',
  'https://publicassets.foreplay.co/api-trusted-brands/hexclad.png'
]

const customPlanIncentives = [
  'Highly discounted API credits.',
  'Priority in-app or Slack support.',
  'Early access to new features & integrations.'
]

const faqContent = [
  {
    question: 'When will my credits be added?',
    answer: 'Your API subscription will be added and charged immediately after clicking “Add API Subscription”. You gain access to the full credit amount immediately.',
  },
  {
    question: 'How are credits calculated?',
    answer: '1 ad = 1 credit, including all metadata associated with that single ad. For instance, consider the following:\n\nExample 1: A query that pulls 100 ad transcripts, uses 100 credits.\n\nExample 2: A query that pulls 100 ad transcripts including video, copy, landing page, etc. also uses 100 credits.'
  },
  {
    question: 'When do my credits renew?',
    answer: 'Credits are renewed on the date of your existing subscription bill date (e.g., If I buy 100k credits mid-billing month, I get 100k immediately and refresh my 100k credits two weeks later).'
  },
  {
    question: 'How does pro-rated billing work?',
    answer: 'There is no price proration.\n\nIf I buy 100k credits mid-billing month, you get two weeks to use those 100k credits before they\'re refreshed.'
  },
  {
    question: 'Do credits carry over?',
    answer: 'There is no carryover of credits of any kind. Trial credits are not carried over, unused credits are not carried over.'
  }
]

export default {
  name: 'ApiCredits',
  mixins: [smoothReflow],
  components: {
    ConfirmCreditPurchaseModal,
    UpdatePaymentMethodModal,
    GiftBoxIcon,
    CheckmarkIcon,
    ContactSupportIcon,
    ChevronIcon,
    CoinIcon
  },
  data() {
    return {
      selectedCreditPackage: null,
      
      // UI Content
      creditPackages,
      trustedBrands,
      customPlanIncentives,
      faqItems: faqContent.map(item => ({
        ...item,
        expanded: false
      })),
      loading: {
        subscribeToAPI: false
      },
      teamOwnerEmail: '',
      tooltipHovered: {
        changePlan: false,
        cancelPlan: false
      },
      showChangePayment: false
    }
  },
  async mounted () {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.applyReflowToAnswers()
      })
    })
    this.teamOwnerEmail = await this.getTeamOwnerEmail()
  },
  beforeDestroy () {
    this.removeReflowFromAnswers()
  },
  computed: {
    ...mapGetters('AuthModule', ['getBasePlanInterval', 'getUserSubscription', 'getStripeCustomer', 'getTeam', 'isTeamOwner', 'getApiSubscription']),
    currentCreditPackage () {
      const currentQuantity = this.getApiSubscription.quantity
      const multiplier = this.creditsMultiplier
      // We want the base package amount
      return currentQuantity / multiplier
    },
    creditsMultiplier () {
      return this.getBasePlanInterval === 'year' ? 2 : 1
    }
  },
  methods: {
    ...mapMutations('MiscModule', ['SET_SUBSCRIPTION_DRAWER_VIEW_MODE']),
    async getTeamOwnerEmail () {
      if (!this.getTeam) return ''
      const team = this.getTeam
      const owner = await FirebaseAPI.Users.getById(team.created_by)

      return owner.email
    },
    async subscribeToAPI (basePackageAmount, packageId) {
      this.loading.subscribeToAPI = packageId
      try {
        const payload = {
          operationType: 'update',
          quantity: basePackageAmount
        }
        const { data } = await ForeplayAPI.Subscription.createAPISubscription(payload)
        this.$showAlert({ type: 'success', message: 'API subscription added' })
      } catch (e) {
        console.error(e)
        const errorType = e.response && e.response.data && e.response.data.error
          ? e.response.data.error
          : 'An unexpected error occurred. You have not been charged.';
        if (errorType === 'payment_method_required') {
          this.$showAlert({type: 'error', message: e.response?.data?.message})
          // Show payment update modal
          this.showChangePayment = true
        } else {
          console.error(e);
          this.$showAlert({ type: 'error', message: 'An unexpected error occurred. Please try again later.' });
        }
      } finally {
        this.loading.subscribeToAPI = null
      }
    },
    showIntercom () {
      window.Intercom('show')
    },
    showSubscriptionDrawer () {
      this.SET_SUBSCRIPTION_DRAWER_VIEW_MODE({ state: true, initInterval: 'annual' })
    },
    // ===== UI Methods =====
    toggleFaqAnswer (index) {
      this.faqItems = this.faqItems.map((item, i) => ({
        ...item,
        expanded: i === index ? !item.expanded : item.expanded
      }))
    },
    applyReflowToAnswers () {
      const answerRefs = this.$refs.faqAnswers
      if (!answerRefs) {
        console.error('No answer refs found')
        return
      }
      answerRefs.forEach(answer => {
        this.$smoothReflow({
          el: answer,
          property: 'height',
          transition: 'height 250ms ease-in-out'
        })
      })
    },
    removeReflowFromAnswers () {
      const answerRefs = this.$refs.faqAnswers
      if (!answerRefs) {
        console.error('No answer refs found')
        return
      }
      answerRefs.forEach(answer => {
        this.$unsmoothReflow({ el: answer })
      })
    },
  }
}
</script>

<style scoped>
.price-text {
  /* Display/H5 */
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
  /* 133.333% */
  letter-spacing: -0.157px;
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: transparent;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}

.secondary-btn:hover {
  box-shadow: none;
  background-color: #F6F8FA;
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}

@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}

.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}

.submit-btn:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}

.submit-btn:disabled {
  background-color: #F6F8FA;
  color: #A4acb9;
  box-shadow: none;
}

.answer-enter-active {
  transition: opacity 250ms ease-in-out;
}

.answer-leave-active {
  transition: opacity 250ms ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.answer-enter-from,
.answer-enter,
.answer-leave-to {
  opacity: 0;
}

.answer-enter-to,
.answer-leave-from {
  opacity: 1;
}

.cancel-btn {
  color: #A4ACB9
}

.btn-underline {
  opacity: 0;
  background-color: #d0d2d6;
  transition: opacity 150ms ease-in-out;
}

.cancel-btn:not([disabled]):hover .btn-underline {
  opacity: 1;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 150ms ease-in-out;
}

.v-enter-from,
.v-enter,
.v-leave-to {
  opacity: 0;
}

.v-enter-to,
.v-leave-from {
  opacity: 1;
}

.label-tooltip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 6px);
  transform: translateX(-50%);
  padding: 8px 12px;
  border-radius: 6px;
  color: white;
  background-color: #131520;
  width: 240px;
  text-align: left;
}
</style>